"use client";

import { createContext, type ReactNode, useContext } from "react";
import type { MessageVariant } from "../widget-config";
import { localSlotThreadKey } from "./local-slots";
import { slotThreadKey } from "./slot-thread";

/** Which MESSAGE slots are local (unknown to the BE) and what their thread headers froze to. */
export interface LocalSlots {
  isLocal: (slotId: string) => boolean;
  /** The persisted header snapshot of a local slot's thread (survives the slot leaving the config). */
  snapshotOf: (keyId: string) => MessageVariant | undefined;
  /** Persist the header snapshot (first wins); the slot is local from then on. */
  remember: (slotId: string, variant: MessageVariant, pinVariant?: boolean) => void;
  /** The thread key of a slot: `slot-<id>` for a local slot, `message:<id>` for a BE slot. */
  keyFor: (slotId: string, variantId?: string) => string;
}

export const INERT_LOCAL_SLOTS: LocalSlots = {
  isLocal: () => false,
  snapshotOf: () => undefined,
  remember: () => {},
  keyFor: (slotId) => slotThreadKey(slotId),
};

/** `variantId` is given only when the BE has the `variantThreads` capability. */
export const slotKeyFor = (local: boolean, slotId: string, variantId?: string) =>
  local ? localSlotThreadKey(slotId, variantId) : slotThreadKey(slotId, variantId);

const Ctx = createContext<LocalSlots>(INERT_LOCAL_SLOTS);

/** `value` must be memoised by the caller. */
export function LocalSlotsProvider({
  value,
  children,
}: {
  value: LocalSlots;
  children: ReactNode;
}) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLocalSlots(): LocalSlots {
  return useContext(Ctx);
}
